/**
 * Pagina pubblica "Chi siamo": forte segnale E-E-A-T per Google e crawler AI.
 * Comunica autorevolezza dell'organizzazione, criteri di selezione dei medici,
 * riferimenti normativi (GDPR, deontologia, telemedicina) e mission.
 *
 * Schema.org: AboutPage + Organization (collegata via @id alla MedicalOrganization
 * dichiarata nella home), in modo da consolidare il "knowledge graph" del sito.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import PublicSEO from '../../components/public/PublicSEO';
import { publicPath } from '../../utils/canonicalUrl';
import { buildBreadcrumb, SITE_URL } from '../../utils/jsonLd';
import './LegalPages.css';
import './ChiSiamoPage.css';

const aboutPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_URL}/chi-siamo#about`,
  url: `${SITE_URL}/chi-siamo`,
  name: 'Chi siamo - Virtual Clinic',
  description: 'Storia, mission, criteri di selezione dei medici e riferimenti normativi di Virtual Clinic (Casa di Cura San Paolo S.p.A.), piattaforma italiana di telemedicina.',
  inLanguage: 'it-IT',
  isPartOf: { '@id': `${SITE_URL}#website` },
  about: { '@id': `${SITE_URL}#organization` },
  mainEntity: { '@id': `${SITE_URL}#medical-organization` },
};

const ChiSiamoPage = () => {
  return (
    <div className="vc-legal-page chi-siamo-page">
      <PublicSEO
        title="Chi siamo - Telemedicina in Italia"
        description="Virtual Clinic (Casa di Cura San Paolo S.p.A.): chi siamo, mission, criteri di selezione dei medici certificati e riferimenti normativi (GDPR, Codice di Deontologia Medica, Accordo Stato-Regioni 2020 sulla telemedicina)."
        canonicalPath="/chi-siamo"
        keywords="chi siamo virtual clinic, telemedicina italia, normativa telemedicina, medici certificati, GDPR sanità"
        ogImageAlt="Chi siamo - Virtual Clinic, telemedicina italiana"
        jsonLd={[
          aboutPageJsonLd,
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Chi siamo' },
          ]),
        ]}
      />

      <div className="container vc-legal-inner chi-siamo-inner">
        <header className="chi-siamo-header">
          <h1>Chi siamo</h1>
          <p className="chi-siamo-lead">
            Virtual Clinic è il marketplace italiano per prenotare visite in studio o in video:
            confronti disponibilità, prezzi e modalità di pagamento, poi confermi con medici
            e strutture verificati, nel rispetto della normativa.
          </p>
        </header>

        <section aria-labelledby="mission-h2">
          <h2 id="mission-h2">La nostra mission</h2>
          <p>
            Rendere trasparente e immediato l&apos;accesso a prestazioni sanitarie private:
            orari aggiornati, prezzi chiari (anche con fondo), prenotazione in pochi passi,
            documenti clinici protetti e colloquio con un professionista certificato.
          </p>
          <p>
            La videovisita affianca, non sostituisce, il rapporto in presenza: è un canale
            in più, non una scorciatoia diagnostica.
          </p>
        </section>

        <section aria-labelledby="azienda-h2">
          <h2 id="azienda-h2">L&apos;azienda</h2>
          <p>
            <strong>Casa di Cura San Paolo Società per Azioni</strong> è la società
            titolare del servizio Virtual Clinic.
          </p>
          <address className="chi-siamo-address">
            Casa di Cura San Paolo<br />
            Società per Azioni<br />
            Via Bonellina 199<br />
            51100 Pistoia – Toscana<br />
            P.IVA: 06640750482<br />
            Tel:{' '}
            <a href="tel:0573380481">0573 380481</a>
            {' · '}
            <a href="tel:0573381518">0573 381518</a>
            <br />
            Email:{' '}
            <a href="mailto:info@casadicurasanpaolo.it">info@casadicurasanpaolo.it</a>
          </address>
        </section>

        <section aria-labelledby="medici-h2">
          <h2 id="medici-h2">Come selezioniamo i medici</h2>
          <p>
            Tutti i professionisti presenti su Virtual Clinic vengono validati prima di
            essere pubblicati sul portale. In particolare verifichiamo:
          </p>
          <ul>
            <li><strong>Iscrizione all&apos;Ordine dei Medici</strong>: ogni medico è
              regolarmente iscritto all&apos;OMCeO della sua provincia e abilitato
              all&apos;esercizio della professione in Italia.</li>
            <li><strong>Titoli di specializzazione</strong>: laurea in Medicina e
              Chirurgia e diploma di specializzazione nella branca dichiarata.</li>
            <li><strong>Assicurazione professionale</strong>: ogni medico è coperto da
              polizza di responsabilità civile professionale come previsto dalla legge
              24/2017 (legge Gelli-Bianco).</li>
            <li><strong>Rispetto del Codice di Deontologia Medica</strong> della FNOMCeO,
              in particolare degli articoli relativi alla telemedicina (artt. 78, 79).</li>
            <li><strong>Curriculum verificato</strong>: università di formazione,
              percorso clinico, esperienze didattiche e/o ospedaliere.</li>
          </ul>
          <p>
            Puoi consultare i profili completi dei professionisti nella sezione{' '}
            <Link to={publicPath('/medici')}>Medici</Link> e i loro ambiti di intervento nella sezione{' '}
            <Link to={publicPath('/discipline')}>Discipline</Link>.
          </p>
        </section>

        <section aria-labelledby="normativa-h2">
          <h2 id="normativa-h2">Riferimenti normativi</h2>
          <p>
            Operiamo nel pieno rispetto della normativa italiana ed europea su
            telemedicina, protezione dei dati personali e prestazioni sanitarie:
          </p>
          <ul>
            <li>
              <strong>Telemedicina:</strong>{' '}
              <a href="https://www.gazzettaufficiale.it/eli/id/2020/12/11/20A06777/sg" target="_blank" rel="noopener noreferrer">
                Accordo Stato-Regioni del 17 dicembre 2020
              </a>{' '}
              recante &quot;Indicazioni nazionali per l&apos;erogazione di prestazioni di
              telemedicina&quot;, e successive <em>Linee guida organizzative del Ministero
              della Salute</em>.
            </li>
            <li>
              <strong>Protezione dei dati:</strong>{' '}
              <a href="https://eur-lex.europa.eu/eli/reg/2016/679/oj" target="_blank" rel="noopener noreferrer">
                Regolamento UE 2016/679 (GDPR)
              </a>{' '}
              e D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018. Le modalità di
              trattamento dei tuoi dati sono descritte in dettaglio nella nostra{' '}
              <Link to="/privacy">Informativa Privacy</Link>.
            </li>
            <li>
              <strong>Sicurezza delle cure:</strong> Legge 24/2017 &quot;Gelli-Bianco&quot;
              sulla responsabilità professionale e sulle coperture assicurative dei
              professionisti sanitari.
            </li>
            <li>
              <strong>Codice di Deontologia Medica</strong> della FNOMCeO (edizione 2014,
              con aggiornamenti) — in particolare gli articoli sulla relazione medico-paziente,
              il segreto professionale e la telemedicina.
            </li>
            <li>
              <strong>Cookie e tracciamento:</strong>{' '}
              <a href="https://www.garanteprivacy.it/cookie" target="_blank" rel="noopener noreferrer">
                Linee guida del Garante Privacy
              </a>{' '}
              del 10 giugno 2021 sull&apos;uso dei cookie. Vedi anche la nostra{' '}
              <Link to="/cookie-policy">Cookie Policy</Link>.
            </li>
          </ul>
        </section>

        <section aria-labelledby="sicurezza-h2">
          <h2 id="sicurezza-h2">Sicurezza e protezione dei dati</h2>
          <p>
            Le videochiamate avvengono su canale cifrato end-to-end. La documentazione
            clinica caricata dai pazienti è archiviata in cloud sicuro su infrastrutture
            con sede nell&apos;Unione Europea e accessibile esclusivamente al medico
            destinatario della visita. Non condividiamo i tuoi dati con terze parti per
            finalità di marketing.
          </p>
        </section>

        <section aria-labelledby="contatti-h2">
          <h2 id="contatti-h2">Hai domande?</h2>
          <p>
            Per qualsiasi richiesta o informazione, puoi scriverci a{' '}
            <a href="mailto:info@casadicurasanpaolo.it">info@casadicurasanpaolo.it</a>{' '}
            oppure usare il modulo nella pagina{' '}
            <Link to={publicPath('/contatti')}>Contatti</Link>.
            <br />
            Tel:{' '}
            <a href="tel:0573380481">0573 380481</a>
            {' · '}
            <a href="tel:0573381518">0573 381518</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default ChiSiamoPage;
